import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { DatePickerWithRange } from '@/components/ui/date-range-picker';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Download, FileText, Table } from 'lucide-react';
import { DateRange } from 'react-day-picker';
import { format } from 'date-fns';
import { ro } from 'date-fns/locale';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { api } from '@/services/api';

interface HospitalStatistics {
  total_patients: number;
  gender_distribution: Array<{ gender: number; count: number }>;
  birth_weight_stats: {
    avg_birth_weight: number;
    min_birth_weight: number;
    max_birth_weight: number;
  };
  gestational_age_stats: {
    avg_gestational_age: number;
    min_gestational_age: number;
    max_gestational_age: number;
  };
  patients_with_diagnoses: number;
  total_diagnoses: number;
  monthly_trend: Array<{ month: string; count: number }>;
}

interface ChdDistribution {
  code: string;
  description: string;
  eurocat_category: string;
  count: number;
  percentage: number;
}

interface SeverityStatistics {
  severity_id: number;
  level_name: string;
  description: string;
  count: number;
  percentage: number;
}

const COLORS = ['#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899'];

export function HospitalReport() {
  const [dateRange, setDateRange] = useState<DateRange | undefined>();
  const [selectedHospital, setSelectedHospital] = useState<string>('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('overview');

  // Report data states
  const [hospitalStats, setHospitalStats] = useState<HospitalStatistics | null>(null);
  const [chdDistribution, setChdDistribution] = useState<ChdDistribution[]>([]);
  const [severityStats, setSeverityStats] = useState<SeverityStatistics[]>([]);
  const [hospitals, setHospitals] = useState<Array<{ hospital_id: number; hospital_name: string }>>([]);

  useEffect(() => {
    fetchHospitals();
  }, []);

  useEffect(() => {
    if (dateRange?.from || dateRange?.to) {
      fetchReportData();
    }
  }, [dateRange, selectedHospital]);

  const fetchHospitals = async () => {
    try {
      const response = await api.get<Array<{ hospital_id: number; hospital_name: string }>>('/api/admin/hospitals');
      if (response.success && response.data) {
        setHospitals(response.data);
      }
    } catch (err) {
      console.error('Error fetching hospitals:', err);
    }
  };

  const fetchReportData = async () => {
    setLoading(true);
    setError(null);

    try {
      const params: any = {};
      if (selectedHospital !== 'all') {
        params.hospital_id = selectedHospital;
      }
      if (dateRange?.from) {
        params.start_date = format(dateRange.from, 'yyyy-MM-dd');
      }
      if (dateRange?.to) {
        params.end_date = format(dateRange.to, 'yyyy-MM-dd');
      }

      // Fetch all reports in parallel
      const [statsRes, chdRes, severityRes] = await Promise.all([
        api.get<HospitalStatistics>('/api/reports/hospital', { params }),
        api.get<ChdDistribution[]>('/api/reports/chd-distribution', { params }),
        api.get<SeverityStatistics[]>('/api/reports/severity-statistics', { params })
      ]);

      if (statsRes.success && statsRes.data) {
        setHospitalStats(statsRes.data);
      }
      if (chdRes.success && chdRes.data) {
        setChdDistribution(chdRes.data);
      }
      if (severityRes.success && severityRes.data) {
        setSeverityStats(severityRes.data);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Eroare la încărcarea rapoartelor');
    } finally {
      setLoading(false);
    }
  };

  const exportReport = async (format: 'pdf' | 'excel') => {
    try {
      const reportData = {
        hospital_statistics: hospitalStats,
        chd_distribution: chdDistribution,
        severity_statistics: severityStats
      };

      const response = await api.post<any>(
        `/api/reports/export/${format}`,
        {
          report_data: reportData,
          report_type: 'hospital',
          title: `Raport Spital - ${format === 'pdf' ? 'PDF' : 'Excel'}`
        },
        {
          responseType: 'blob'
        }
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data as BlobPart]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `raport_spital_${format === 'pdf' ? '.pdf' : '.xlsx'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Error exporting report:', err);
    }
  };

  const formatGenderData = (data: Array<{ gender: number; count: number }>) => {
    return data.map(item => ({
      name: item.gender === 0 ? 'Feminin' : 'Masculin',
      value: item.count
    }));
  };

  const formatMonthlyTrend = (data: Array<{ month: string; count: number }>) => {
    return data.map(item => ({
      month: format(new Date(item.month + '-01'), 'MMM yyyy', { locale: ro }),
      count: item.count
    }));
  };

  const formatNumber = (num: number | undefined | null, decimals: number = 1) => {
    if (typeof num === 'number' && !isNaN(num)) {
      return num.toFixed(decimals);
    }
    return '0';
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Rapoarte spital</CardTitle>
          <CardDescription>
            Vizualizează statistici și rapoarte detaliate pentru spital
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <Select value={selectedHospital} onValueChange={setSelectedHospital}>
              <SelectTrigger className="w-full md:w-[250px]">
                <SelectValue placeholder="Selectează spital" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toate spitalele</SelectItem>
                {hospitals.filter(h => h && h.hospital_id).map(hospital => (
                  <SelectItem key={hospital.hospital_id} value={hospital.hospital_id.toString()}>
                    {hospital.hospital_name || `Spital ${hospital.hospital_id}`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <DatePickerWithRange
              date={dateRange}
              onDateChange={setDateRange}
            />

            <div className="flex gap-2 ml-auto">
              <Button
                variant="outline"
                size="sm"
                onClick={() => exportReport('pdf')}
                disabled={!hospitalStats}
              >
                <FileText className="h-4 w-4 mr-2" />
                Export PDF
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => exportReport('excel')}
                disabled={!hospitalStats}
              >
                <Table className="h-4 w-4 mr-2" />
                Export Excel
              </Button>
            </div>
          </div>

          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : hospitalStats ? (
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="overview">Prezentare generală</TabsTrigger>
                <TabsTrigger value="diagnoses">Diagnostice MCC</TabsTrigger>
                <TabsTrigger value="severity">Severitate</TabsTrigger>
                <TabsTrigger value="trends">Tendințe</TabsTrigger>
              </TabsList>

              <TabsContent value="overview" className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium">Total pacienți</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">{hospitalStats.total_patients}</div>
                      <p className="text-xs text-muted-foreground">
                        {hospitalStats.patients_with_diagnoses} cu diagnostice
                      </p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium">Total diagnostice</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">{hospitalStats.total_diagnoses}</div>
                      <p className="text-xs text-muted-foreground">
                        Media: {hospitalStats.total_diagnoses && hospitalStats.patients_with_diagnoses
                          ? formatNumber(hospitalStats.total_diagnoses / hospitalStats.patients_with_diagnoses)
                          : '0'} per pacient
                      </p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium">Greutate Medie la Naștere</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">
                        {formatNumber(hospitalStats.birth_weight_stats?.avg_birth_weight, 0)} g
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Min: {hospitalStats.birth_weight_stats?.min_birth_weight || 'N/A'} g |
                        Max: {hospitalStats.birth_weight_stats?.max_birth_weight || 'N/A'} g
                      </p>
                    </CardContent>
                  </Card>
                </div>

                {hospitalStats.gender_distribution && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm font-medium">Distribuție pe gen</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                          <Pie
                            data={formatGenderData(hospitalStats.gender_distribution)}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ name, percent }) => `${name}: ${formatNumber((percent || 0) * 100, 0)}%`}
                            outerRadius={80}
                            fill="#8884d8"
                            dataKey="value"
                          >
                            {formatGenderData(hospitalStats.gender_distribution).map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>

              <TabsContent value="diagnoses" className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm font-medium">Distribuție diagnostice MCC</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={400}>
                      <BarChart data={chdDistribution.slice(0, 10)}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="code" />
                        <YAxis />
                        <Tooltip
                          content={({ active, payload }) => {
                            if (active && payload && payload[0]) {
                              const data = payload[0].payload;
                              return (
                                <div className="bg-white p-2 border rounded shadow">
                                  <p className="font-semibold">{data.code}</p>
                                  <p className="text-sm">{data.description}</p>
                                  <p className="text-sm">Cazuri: {data.count}</p>
                                  <p className="text-sm">Procent: {data.percentage}%</p>
                                </div>
                              );
                            }
                            return null;
                          }}
                        />
                        <Bar dataKey="count" fill="#3b82f6" />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="severity" className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm font-medium">Distribuție severitate</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={severityStats}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ level_name, percentage }) => `${level_name}: ${percentage}%`}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="count"
                        >
                          {severityStats.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="trends" className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm font-medium">Tendință Înregistrări Lunare</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={formatMonthlyTrend(hospitalStats.monthly_trend)}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Line
                          type="monotone"
                          dataKey="count"
                          stroke="#3b82f6"
                          name="Pacienți noi"
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              Selectează o perioadă pentru a genera raportul
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}